import { useAuth0 } from '@auth0/auth0-react';
import classnames from 'classnames';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Input, Label } from 'reactstrap';
import { BackendService, Work } from '../service/backend-service';
import { useStoreDispatch } from '../store/hooks';
import { updateWork, addWorkSprints} from '../store/store-core';
import { errorMessage } from '../util/error-utils';
import { StyledToast } from '../util/styled-toast';
import StopWatch from './stop-watch';

function PunchContainer({
  work
}: {
  work: Work
}) {
  let auth0 = useAuth0();
  const service = useMemo(() => {
    return new BackendService(auth0);
  }, [auth0]);

  const [workDescription, setWorkDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSprint, setSelectedSprint] = useState(Math.max(...work.activeSprints));
  let dispatch = useStoreDispatch();

  useEffect(() => {
    service.getLatestPunch(work.project.id, selectedSprint).then(punch => {
      if (punch) {
        setWorkDescription(punch.workDescription)
      }
    }).catch((ex: any) => StyledToast.error(errorMessage(ex)));
  }, [service, work.project.id, selectedSprint]);


  function punch() {
    setLoading(true);
    let time = moment().utc().format();
    let opening = !!!work.openPunch;
    let openPunch = work.openPunch;
    if (!!!openPunch) {
      //  start a new punch
      openPunch = {
        start: time,
        sprintId: selectedSprint,
        workDescription: workDescription
      }
    }

    service.punch(work.project, openPunch)
      .then(bePunch => 
        dispatch(updateWork({
        projectId: work.project.id,
        openPunch: opening ? bePunch : undefined
      })))
      .catch((ex: any) => StyledToast.error(errorMessage(ex)))
      .finally(() => setLoading(false));
  }

  function changeSprint(event: React.ChangeEvent<HTMLSelectElement>) {
    if (event.target.value === "-1") {
      service.createSprint(work.project)
        .then(sprint => {
          dispatch(addWorkSprints({
            newSprint: sprint.id,
            projectId: work.project.id
          }))
        }).catch((e: any) => StyledToast.error(errorMessage(e)))
    } else {
      setSelectedSprint(Number.parseInt(event.target.value))
    }
  }

  function renderBody() {
    if (loading) {
      return (
        <div className="align-items-center d-flex justify-content-center loading-spinner w-100 h-100">
          <div className={"spinner-border spinner-border-lg text-" + work.project.color} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    let fragment;
    if (!!!work.openPunch) {
      fragment = <div className="form-floating mt-3 mb-2">
        <Input placeholder="Work Description" type="textarea" style={{ height: '92px' }} 
               value={workDescription} 
               onChange={(e) => setWorkDescription(e.target.value)} />
        <Label><i className='far fa-fw fa-comment-dots me-2' />Work Description</Label>
      </div>
    } else {
      fragment = <div className='text-center'>
        <div><b>Started on:</b> {moment(work.openPunch.start).format('lll')}</div>
        <StopWatch startOffset={work.openPunch.start} />
        {
          !!!work.openPunch.workDescription 
            ? null 
            : <div title={work.openPunch.workDescription} className='text-truncate mt-2'>
                <i className='far fa-fw fa-comment-dots me-2'/>{work.openPunch.workDescription}
              </div>  
        }
      </div>
    }

    return (
      <>
        <div className='text-center mb-2'>
          <i className={classnames({
            'fas fa-fw stop-start-button': true,
            'text-primary fa-circle-play': !!!work.openPunch,
            'text-danger fa-circle-stop': work.openPunch,
          })} onClick={punch} />
        </div>
        {fragment}
      </>
    )

  }

  return (
    <div className='col mb-3'>
      <Card className='h-100'>
        <CardHeader className={'alert-' + work.project.color}>
          {
            work.project.favorite ? <i className='fas fa-fw fa-heart text-danger me-2' /> : null
          }
          <span className='fw-bold'>{work.project.name}</span>
          {
            !!!work.openPunch
              ? <select className={'form-select float-end header-select alert-' + work.project.color} 
                  value={selectedSprint} 
                  onChange={changeSprint}>
                {
                  work.activeSprints
                    .map(sprint => <option key={sprint} value={sprint}>Sprint {sprint}</option>)
                }
                <option value="-1">New ...</option>  
              </select>
              : <>
                  <span className='float-end'>Sprint <b>{work.openPunch.sprintId}</b></span>
                </>
          }
        </CardHeader>
        <CardBody>
          {
            renderBody()
          }
        </CardBody>
      </Card>
    </div >
  );
}

export default PunchContainer;
